import { useEffect, useRef } from 'react';
import { Alert, Button, Form, type FormInstance } from 'antd';
import type { ModelReleaseDetail } from '@apis/model';
import {
  isParameterApplicable, isUserTaskTypeSwitch, MODEL_RELEASE_PARAM_KEY, parameterValueError,
  shouldClearModelReleaseOnTaskTypeChange, type CustomParameterDefinitions,
} from '@/utils/jobCustomParameters';
import ModelReleasePicker from './ModelReleasePicker';

/** 目录未就绪时的兜底文案：加载中不提示，失败才提示并给重试。 */
const UNAVAILABLE_HINT = '参数目录加载失败，模型主版本暂不可用；不使用该参数的任务不受影响。';

/**
 * 顶层「模型主版本」字段：与 Job 名称同级、渲染在其正上方。
 *
 * 挂载口径（正确性核心）：**有值就一律挂载**。目录缺失/停用/不适用/拉取失败时若直接不渲染，
 * 复制的 Job 仍能提交、但 ``customParams.model_release`` 会悄悄消失 —— 那是静默语义回退。
 * 改为挂载并拦住提交（给出具体原因），picker 自带 allowClear 给用户出口。
 * 只有「没有值、且确定不适用」才不渲染。
 *
 * 唯一例外是**用户主动切换任务类型**：新任务类型不适用时把已选版本清掉（字段随之消失）。
 * 留着既提交不了也没意义，实测反馈就是这个口径（选完感知仿真再换成别的任务，版本还挂着）。
 * 复制回填、数据更新弹窗解析任务类型这些**非用户动作**绝不清 —— 判定口径见
 * ``shouldClearModelReleaseOnTaskTypeChange``（已单测），组件只负责接线。
 *
 * 加载中与拉取失败都算**未知**、不能当成「不适用」：`isParameterApplicable` 在
 * ``taskTypeIds`` 非空而 ``taskTypeId`` 未定时返回 false（`Number(undefined)` → NaN），
 * 恰好会被误判；数据更新弹窗的任务类型来自 state、存在未定的渲染帧。
 */
export default function ModelReleaseField({
  form, taskTypeId, active, required = false, definitions, loading, error, onReload, loadRelease,
}: {
  form: FormInstance;
  taskTypeId?: number;
  active: boolean;
  /** 必填（感知数据刷写）：不适用时也渲染并提示去配置，避免字段隐身后只在提交时被后端拒绝。 */
  required?: boolean;
  definitions: CustomParameterDefinitions;
  loading: boolean;
  error: boolean;
  onReload: () => void;
  loadRelease: (name: string) => Promise<ModelReleaseDetail>;
}) {
  const definition = definitions[MODEL_RELEASE_PARAM_KEY];
  // preserve 必须为 true：默认只读已挂载字段的值，而本字段的挂载与否又取决于该值，会互相死锁。
  const value = Form.useWatch('modelRelease', { form, preserve: true }) as string | undefined;

  const settled = !loading && !error;
  const applicable = settled && !!definition && isParameterApplicable(definition, taskTypeId);
  const hasValue = typeof value === 'string' && value !== '';
  const disabledReason = !definition
    ? '模型主版本参数已停用或不存在，请清除后继续。'
    : '模型主版本不适用于当前任务类型，请清除或改选任务类型。';

  // 切换任务类型时清掉不再适用的已选版本（实测反馈：选完感知仿真再换成别的任务，版本还挂着）。
  // 判定口径全在 shouldClearModelReleaseOnTaskTypeChange 里（已单测），这里只负责：
  // 盯住任务类型变化、把「用户主动切换」暂存下来、等目录就绪后按同一套逻辑判一次。
  // 暂存是必要的：目录还在路上时换了类型，当时判不了，若不留痕就永远补不上。
  const lastTaskTypeIdRef = useRef<number | undefined>(taskTypeId);
  const pendingSwitchRef = useRef<{ from?: number; to?: number } | null>(null);
  useEffect(() => {
    if (!active) return;
    const previousTaskTypeId = lastTaskTypeIdRef.current;
    if (previousTaskTypeId !== taskTypeId) {
      lastTaskTypeIdRef.current = taskTypeId;
      if (isUserTaskTypeSwitch(previousTaskTypeId, taskTypeId)) {
        pendingSwitchRef.current = { from: previousTaskTypeId, to: taskTypeId };
      }
    }
    const pending = pendingSwitchRef.current;
    if (!pending) return;
    // 目录未就绪：不判断、也不清暂存，等它就绪后补判
    const definitionKnown = settled && !!definition;
    if (!definitionKnown) return;
    pendingSwitchRef.current = null;
    if (!shouldClearModelReleaseOnTaskTypeChange({
      previousTaskTypeId: pending.from,
      taskTypeId: pending.to,
      hasValue,
      definitionKnown,
      applicable,
    })) return;
    form.setFieldValue('modelRelease', undefined);
  }, [active, applicable, definition, form, hasValue, settled, taskTypeId]);

  // 目录状态或适用性变化后重跑校验：否则「暂不可用，请重试」会在恢复后粘住、旧错误也不消失。
  useEffect(() => {
    if (active && form.getFieldError('modelRelease').length) {
      void form.validateFields(['modelRelease']).catch(() => {});
    }
  }, [active, applicable, definitions, error, form, hasValue, loading, taskTypeId]);

  if (!hasValue && !applicable && !error && !required) return null;

  return (
    <>
      <Form.Item
        name="modelRelease"
        label={definition?.label || '模型主版本'}
        tooltip={definition?.description || undefined}
        required={required}
        rules={[{
          validator: async (_, raw: unknown) => {
            if (raw === undefined || raw === null || raw === '') {
              if (required) throw new Error('请选择模型主版本');
              // 可选字段：不选即不传 customParams.model_release，走任务默认行为。
              return;
            }
            const valueError = parameterValueError(raw, 'model_release');
            if (valueError) throw new Error(valueError);
            if (loading || error) throw new Error('自定义参数暂不可用，请重试加载后继续；也可清除该字段。');
            if (!definition) throw new Error('模型主版本参数已停用或不存在，请清除后继续。');
            if (taskTypeId === undefined) throw new Error('请先选择任务类型');
            if (!isParameterApplicable(definition, taskTypeId)) {
              throw new Error('模型主版本不适用于当前任务类型，请清除或改选任务类型。');
            }
            try {
              await loadRelease(raw as string);
            } catch {
              throw new Error(`模型版本 ${String(raw)} 不可用，请重试或重新选择`);
            }
          },
        }]}
      >
        <ModelReleasePicker loadRelease={loadRelease} />
      </Form.Item>
      {error && (
        <div style={{ marginTop: -8, marginBottom: 16 }}>
          <Alert showIcon type="info" message={UNAVAILABLE_HINT} action={
            <Button size="small" onClick={onReload}>重试</Button>
          } />
        </div>
      )}
      {hasValue && settled && !applicable && (
        <div style={{ marginTop: -8, marginBottom: 16 }}>
          <Alert showIcon type="warning" message={disabledReason} />
        </div>
      )}
      {required && !hasValue && settled && !applicable && (
        <div style={{ marginTop: -8, marginBottom: 16 }}>
          <Alert
            showIcon
            type="warning"
            message="模型主版本尚未对当前任务类型开放，请管理员在「系统参数运维 · 任务自定义参数」里把本任务类型加入模型主版本的适用范围。"
          />
        </div>
      )}
    </>
  );
}
